import type { CustomFunctionUiActionApi_UiActionDto } from "src/api/generated/erp/customFunctions/triggers/uiAction/api/customFunctionUiActionApi.ts";
import type { CustomFunctionUiActionContext } from "src/api/generated/erp/customFunctions/triggers/uiAction/context/customFunctionUiActionContext.ts";
import type { CustomFunctionUiActionContextItem } from "src/api/generated/erp/customFunctions/triggers/uiAction/context/customFunctionUiActionContextItem.ts";
import type { CustomFunctionUiActionView } from "src/api/generated/io/aavo/applications/db/erp/types/customFunctionUiActionView.ts";

type SelectableUiAction = {
	locations: CustomFunctionUiActionView[];
	objectCardinality: CustomFunctionUiActionApi_UiActionDto["objectCardinality"];
};

export interface CustomUiActionWithContext<T extends SelectableUiAction = SelectableUiAction> {
	action: T;
	executionContext: CustomFunctionUiActionContext;
}

export function selectCustomUiActions<T extends SelectableUiAction>(
	actions: T[],
	view: CustomFunctionUiActionView,
	contextItems: CustomFunctionUiActionContextItem[],
): CustomUiActionWithContext<T>[] {
	return actions.flatMap<CustomUiActionWithContext<T>>((action) => {
		if (!action.locations.includes(view)) return [];

		switch (action.objectCardinality) {
			case "ZERO":
				return contextItems.length === 0 ? [{ action, executionContext: { cardinality: "ZERO" } }] : [];
			case "ONE": {
				const item = contextItems[0];
				return contextItems.length === 1 && item != null ?
						[{ action, executionContext: { cardinality: "ONE", item } }]
					:	[];
			}
			case "ONE_OR_MORE":
				return contextItems.length === 0 ?
						[]
					:	[{ action, executionContext: { cardinality: "ONE_OR_MORE", items: contextItems } }];
		}
	});
}

export function selectCustomUiActionsIncludingZero<T extends SelectableUiAction>(
	actions: T[],
	view: CustomFunctionUiActionView,
	contextItems: CustomFunctionUiActionContextItem[],
): CustomUiActionWithContext<T>[] {
	const zeroCardinalityActions = selectCustomUiActions(actions, view, []);
	if (contextItems.length === 0) return zeroCardinalityActions;
	return [...zeroCardinalityActions, ...selectCustomUiActions(actions, view, contextItems)];
}
